import React, {PropsWithChildren, ReactElement} from 'react';
import PlaceholderElement from './placeholder/PlaceholderElement';
import ElementStaticTableComponentV2 from './table/staticTableV2';
import ElementStaticTextComponent from './text/staticText';
import ElementHtmlComponent from './text/html';
import ElementStaticListComponent from './list/staticList';
import ElementStaticTableComponent from './table/staticTable';
import ElementImageComponent from './image/image';
import ElementVideoComponent from './video/video';
import ElementRectangleComponent from './geometry/rectangle';
import ElementCircleComponent from './geometry/circle';
import ElementButtonComponent from './button/button';
import ElementTextInput from './input/textInput';
import FormElementComponent from './form/formElement';
import ElementModalComponent from './modal/modal';
import ElementImageGalleryComponent from './imageGallery/imageGallery';
import ElementLineComponent from './geometry/line';
import TriangleElement from './geometry/TriangleElement';
import StarElement from './geometry/StarElement';
import ArrowElement from './geometry/ArrowElement';
import Animated from './animated/animated';
import QuestionElement from './question/question/QuestionElement';
import YesNoQuestionElement from './question/yesNoQuestion/YesNoQuestionElement';
import MultiselectQuestionElement from './question/multiselect/MultiselectQuestionElement';
import PhotoAnswerQuestionElement from './question/photoAnswer/PhotoAnswerQuestionElement';
import QuestionReportElement from './question/questionReport/QuestionReportElement';
import SequenceQuestionElement from './question/sequence/SequenceQuestionElement';
import ImageMarkupQuestionElement from './question/imageMarkup/ImageMarkupQuestionElement';
import ElementFlipCardComponent from './flipCard/flipCard';
import MatchPairQuestionElement from './question/matchPair/MatchPairQuestionElement';
import LikertScaleQuestionElement from './question/likertScale/LikertScaleQuestionElement';
import {ELEMENT} from '../../incase-domain/elements/base/ElementTypes';
import {IElement} from '../../incase-domain/elements';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import CalculatorElementComponent from './calculator/CalculatorElementComponent';
import YesNoQuestionElementV2 from './question/yesNoQuestion/YesNoQuestionElementV2';
import LineElementV2 from './geometry/LineElementV2';
import ChatbotElementComponent from './chatbot/ChatbotElementComponent';
import ElementEllipseComponent from './geometry/ellipse';

interface ElementTypedProps {
    element: IElement;
    device?: DeviceType;
}

function ElementTyped(props: PropsWithChildren<ElementTypedProps>): ReactElement {
    const {element, children, device = DeviceType.DESKTOP} = props;
    const {type} = element;

    const elementsThatCanBeRotated = [ELEMENT.ELLIPSE, ELEMENT.RECTANGLE, ELEMENT.TRIANGLE, ELEMENT.STAR, ELEMENT.LINE, ELEMENT.ARROW];

    let elementComponent;

    switch (type) {
        case ELEMENT.STATIC_TEXT:
            elementComponent = <ElementStaticTextComponent element={element} device={device} />;
            break;
        case ELEMENT.HTML:
            elementComponent = <ElementHtmlComponent element={element} device={device} />;
            break;
        case ELEMENT.STATIC_LIST:
            elementComponent = <ElementStaticListComponent element={element} device={device} />;
            break;
        case ELEMENT.STATIC_TABLE_V2:
            elementComponent = <ElementStaticTableComponentV2 element={element} />;
            break;
        case ELEMENT.STATIC_TABLE:
            elementComponent = <ElementStaticTableComponent element={element} device={device} />;
            break;
        case ELEMENT.IMAGE:
            elementComponent = <ElementImageComponent element={element} />;
            break;
        case ELEMENT.IMAGE_GALLERY:
            elementComponent = <ElementImageGalleryComponent element={element} />;
            break;
        case ELEMENT.VIDEO:
            elementComponent = <ElementVideoComponent element={element} />;
            break;
        case ELEMENT.QUESTION:
            elementComponent = <QuestionElement element={element} device={device} />;
            break;
        case ELEMENT.YES_NO_QUESTION:
            elementComponent = <YesNoQuestionElement element={element} device={device} />;
            break;
        case ELEMENT.YES_NO_QUESTION_V2:
            elementComponent = <YesNoQuestionElementV2 element={element} device={device} />;
            break;
        case ELEMENT.SEQUENCE_QUESTION:
            elementComponent = <SequenceQuestionElement element={element} device={device} />;
            break;
        case ELEMENT.MULTI_SELECT:
            elementComponent = <MultiselectQuestionElement element={element} device={device} />;
            break;
        case ELEMENT.MATCH_PAIR_QUESTION:
            elementComponent = <MatchPairQuestionElement element={element} device={device} />;
            break;
        case ELEMENT.LIKERT_SCALE:
            elementComponent = <LikertScaleQuestionElement element={element} device={device} />;
            break;
        case ELEMENT.QUESTION_REPORT:
            elementComponent = <QuestionReportElement element={element} device={device} />;
            break;
        case ELEMENT.RECTANGLE:
            elementComponent = <ElementRectangleComponent element={element} />;
            break;
        case ELEMENT.CIRCLE:
            elementComponent = <ElementCircleComponent element={element} />;
            break;
        case ELEMENT.ELLIPSE:
            elementComponent = <ElementEllipseComponent element={element} />;
            break;
        case ELEMENT.LINE:
            elementComponent = <ElementLineComponent element={element} />;
            break;
        case ELEMENT.LINE_V2:
            elementComponent = <LineElementV2 element={element} />;
            break;
        case ELEMENT.TRIANGLE:
            elementComponent = <TriangleElement color={element.color} fill={element.fill} />;
            break;
        case ELEMENT.STAR:
            elementComponent = <StarElement color={element.color} fill={element.fill} />;
            break;
        case ELEMENT.ARROW:
            elementComponent = <ArrowElement color={element.color} />;
            break;
        case ELEMENT.BUTTON:
            elementComponent = <ElementButtonComponent element={element} device={device} />;
            break;
        case ELEMENT.TEXT_INPUT:
            elementComponent = <ElementTextInput element={element} device={device} />;
            break;
        case ELEMENT.FORM:
            elementComponent = <FormElementComponent element={element} device={device} />;
            break;
        case ELEMENT.MODAL:
            elementComponent = <ElementModalComponent element={element} />;
            break;
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            elementComponent = <PhotoAnswerQuestionElement element={element} device={device} />;
            break;
        case ELEMENT.IMAGE_MARKUP:
            elementComponent = <ImageMarkupQuestionElement element={element} device={device} />;
            break;
        case ELEMENT.FLIP_CARD:
            elementComponent = <ElementFlipCardComponent element={element} device={device} />;
            break;
        case ELEMENT.CALCULATOR:
            elementComponent = <CalculatorElementComponent element={element} device={device} />;
            break;
        case ELEMENT.CHATBOT:
            elementComponent = <ChatbotElementComponent element={element} />;
            break;
        case ELEMENT.PLACEHOLDER:
            elementComponent = <PlaceholderElement element={element} device={device} />;
            break;
        default:
            elementComponent = <div>Undefined element</div>;
    }
    return (
        <>
            <Animated
                style={{width: '100%', height: '100%', position: 'relative'}}
                animationIn={element.elementAnimations?.entry}
                isVisible
            >
                {/* TODO: Try to move this logic to the parent component; remove children prop if not needed */}
                {elementsThatCanBeRotated.includes(element.type) && children}
                <div style={{width: '100%', height: '100%'}}>{elementComponent}</div>
            </Animated>
        </>
    );
}

export default ElementTyped;
